/**
 * Binary protocol parsers matching firmware byte layouts.
 *
 * Alert packet (8 bytes) — main.cpp:692-707:
 *   [0]    AlertType enum (0-6)
 *   [1]    WiFi AP count
 *   [2]    BLE device count
 *   [3]    strongest RSSI + 128 (unsigned shift)
 *   [4..7] uptime ms (big-endian uint32)
 *
 * Spectrum (126 bytes) — main.cpp:713:
 *   One byte per nRF24 channel (values 0-2)
 */

import { AlertType, ALERT_SEVERITY } from '../types/alerts';
import type { Alert } from '../types/alerts';
import type { SpectrumFrame } from '../types/spectrum';
import { NRF_NUM_CHANNELS } from '../types/spectrum';

export function parseAlertPacket(buffer: DataView): Alert | null {
  if (buffer.byteLength < 8) return null;

  const typeRaw = buffer.getUint8(0);
  if (typeRaw > 6) return null;

  const type = typeRaw as AlertType;
  const wifiApCount = buffer.getUint8(1);
  const bleDeviceCount = buffer.getUint8(2);
  // Firmware shifts RSSI by +128 to make it unsigned
  const strongestRssi = buffer.getUint8(3) - 128;
  // Big-endian uint32 uptime
  const uptimeMs = buffer.getUint32(4, false);

  return {
    type,
    severity: ALERT_SEVERITY[type],
    wifiApCount,
    bleDeviceCount,
    strongestRssi,
    uptimeMs,
    receivedAt: Date.now(),
  };
}

export function parseSpectrumData(buffer: DataView): SpectrumFrame | null {
  if (buffer.byteLength < NRF_NUM_CHANNELS) return null;

  const channels = new Uint8Array(NRF_NUM_CHANNELS);
  for (let i = 0; i < NRF_NUM_CHANNELS; i++) {
    channels[i] = buffer.getUint8(i);
  }

  return {
    channels,
    receivedAt: Date.now(),
  };
}
